import { Box } from '@material-ui/core';
import { useAuth } from '@nemuru-eng/providers';

import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { ProfileAvatar } from '@/components/shared/components/ProfileAvatar';
import { ProfileSwitchOverlay } from '@/components/shared/components/ProfileSwitchOverlay/ProfileSwitchOverlay';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { StatusTagType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useProfileSwitch } from '@/hooks/useProfileSwitch';

/** The profiles of the person behind the session, with the active one marked; only when there are two or more (spec §7). */
export const ActiveProfileSection = () => {
  const { activeProfile, profiles } = useAuth();
  const { switchingProfile, switchingTo, switchTo } = useProfileSwitch();
  const { palette, customBorderRadius } = useCustomTheme();

  if (!profiles || profiles.length < 2) return null;

  return (
    <SubSectionWrapper title={'ACTIVE_PROFILE'}>
      <Box mt={-1} mb={2}>
        <Font variant="caption" color="textSecondary">
          <Text tId={'ACTIVE_PROFILE_CAPTION'} dynamicDataTexts={[String(profiles.length)]} />
        </Font>
      </Box>
      <CustomPaper fill="paper">
        <FlexColumn width={'100%'} gridGap={0} alignItems="stretch">
          {profiles.map((profile, index) => {
            const isActive = profile.id === activeProfile?.id;
            return (
              <Box key={profile.id}>
                {index > 0 && <CustomDivider width={'100%'} margin={0} />}
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={16}
                  px={2}
                  py={2}
                  bgcolor={isActive ? palette.action.hover : 'transparent'}
                  borderRadius={isActive ? customBorderRadius.paper : 0}
                >
                  <ProfileAvatar profile={profile} />
                  <Box display="flex" flexDirection="column" minWidth={0} flex={1}>
                    <Font variant="subtitle2" color="textPrimary" noWrap>
                      <strong>{profile.displayName ?? profile.username}</strong>
                    </Font>
                    <Font variant="caption" color="textSecondary" noWrap>
                      {profile.description ?? <Text tId={`user_${profile.userType}`} />}
                    </Font>
                  </Box>
                  <Box minWidth={128} display="flex" justifyContent="flex-end" flexShrink={0}>
                    {isActive ? (
                      <StatusTag labelTextId={'ACTIVE_PROFILE_TAG'} type={StatusTagType.SUCCESS} size="medium" />
                    ) : (
                      <StandardButton
                        size="small"
                        variant="outlined"
                        onClick={() => switchTo(profile.id)}
                        loading={switchingTo === profile.id}
                        disabled={switchingTo !== undefined}
                      >
                        <Text tId={'CHANGE'} />
                      </StandardButton>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </FlexColumn>
      </CustomPaper>
      <ProfileSwitchOverlay profile={switchingProfile} open={switchingProfile !== undefined} />
    </SubSectionWrapper>
  );
};
